<template>
  <div data-spec="main">
    <section data-test="product-results-refinement">
      <div class="mb-12 justify-between lg:mb-32 lg:flex">
        <span />
      </div>
      <div class="sticky -top-1 z-[1]">
        <TopFilters
          v-model:filters="filters"
          v-model:price="price"
          v-model:sort="sort"
          :facets
          :is-pending
          :price-facet
          :search-nb-results="productsSearchData.total"
          :sort-options
        />
      </div>

      <div class="mt-16 lg:flex lg:gap-16">
        <TheMainContent class="w-full">
          <ResultsCount :is-pending :total="productsSearchData.total" />
          <SwapPill v-if="isSwapEnabled" class="lg:hidden" />

          <NoResults
            v-if="productsSearchData.products.length === 0"
            class="my-32"
            data-spec="body"
          />
          <ResultGrid
            v-else
            class="mt-8 md:mt-32"
            :is-pending
            :products="productsSearchData.products"
            with-payment-advertisement
          />
        </TheMainContent>
      </div>

      <Pagination v-model="page" :page-count="productsSearchData.pageCount" />
    </section>

    <FullscreenFilters
      v-model:filters="filters"
      v-model:price="price"
      v-model:sort="sort"
      :facets
      :price-facet
      :search-nb-results="productsSearchData.total"
      :sort-options
    />
  </div>
</template>

<script setup lang="ts">
import { createError, useRouteParams } from '#imports'
import { ref } from 'vue'

import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'
import NoResults from '~/scopes/product-list/components/NoResults/NoResults.vue'
import Pagination from '~/scopes/product-list/components/Pagination/Pagination.vue'
import ResultGrid from '~/scopes/product-list/components/ResultGrid/ResultGrid.vue'
import { useFiltersFromUrl } from '~/scopes/product-list/composables/useFiltersFromUrl'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'

import ResultsCount from '../../../../components/ResultsCount/ResultsCount.vue'
import { useProductsSearch } from '../../../../composables/useProductsSearch/useProductsSearch'
import { useSearchState } from '../../../../composables/useSearchState/useSearchState'

const { uuid: sellerId } = useRouteParams()
const { isSwapEnabled } = useSwapEnabled()

const { data: searchConfig } = await useAlgoliaConfiguration(
  'merchant-landing',
  sellerId,
)

if (searchConfig.value === undefined) {
  throw createError({
    status: 404,
    statusText: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const sortOptions = getSortOptions(searchConfig.value.indexes)

const { filters, sort, price, page } = useSearchState(searchConfig.value)

const priceFacet = ref<UiPriceFacet | undefined>()
const facets = ref<Facet[]>([])

const { data: productsSearchData } = await useProductsSearch(
  sellerId,
  searchConfig.value,
  {
    filtersInput: filters,
    priceInput: price,
    sortInput: sort,
    pageInput: page,
  },
)

// @TODO: Replace this with a real loading state
const isPending = ref(false)

facets.value = productsSearchData.value.facets
priceFacet.value = productsSearchData.value.priceFacet

useFiltersFromUrl(filters, price, page)
</script>
